import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Space,
  Image,
  Tabs,
  App,
  Avatar,
  Tag,
  Divider,
  Form
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
  PictureOutlined,
  PlusOutlined,
  CopyOutlined
} from '@ant-design/icons';
import { 
  getRoutineById, 
  deleteRoutine,
  getRoutineDays,
  createRoutineDay,
  updateRoutineDay,
  deleteRoutineDay,
  duplicateRoutineWeek,
  publishRoutine,
  listRoutineDayExercises,
  normalizeRoutineDayExercisesResponse,
} from '../../services/routines';
import { resolveMedia } from '../../utils/media';
import RoutineDayModal from './RoutineDayModal';

const { Title, Text, Paragraph } = Typography;
const { useApp } = App;

const RoutineConfigurePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { message, modal } = useApp();

  const [loading, setLoading] = useState(false);
  const [routine, setRoutine] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [routineDays, setRoutineDays] = useState([]);
  const [loadingDays, setLoadingDays] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingDay, setEditingDay] = useState(null);
  const [form] = Form.useForm();
  const [exerciseCounts, setExerciseCounts] = useState({});

  useEffect(() => {
    if (!id) {
      message.error('ID de rutina no proporcionado');
      navigate('/routines/all');
      return;
    }

    const loadRoutine = async () => {
      setLoading(true);
      try {
        const routineData = await getRoutineById(id);
        setRoutine(routineData);
      } catch (error) {
        message.error('No se pudo cargar la rutina');
        navigate('/routines/all');
      } finally {
        setLoading(false);
      }
    };

    loadRoutine();
  }, [id, navigate, message]);

  // Cargar días de la rutina cuando se cambia al tab de programa
  useEffect(() => {
    if (activeTab === 'program' && id) {
      loadRoutineDays();
    }
  }, [activeTab, id]);

  const loadRoutineDays = async () => {
    if (!id) return;
    setLoadingDays(true);
    try {
      const response = await getRoutineDays(id);
      const days = response?.items || response || [];
      setRoutineDays(days);
      
      // Cargar conteo de ejercicios para cada día
      const counts = {};
      const exercisePromises = days.map(async (day) => {
        try {
          const raw = await listRoutineDayExercises(id, day.id);
          const { exerciseGroups, standaloneExercises } = normalizeRoutineDayExercisesResponse(raw);
          const inGroups = exerciseGroups.reduce(
            (n, g) => n + (Array.isArray(g.exercises) ? g.exercises.length : 0),
            0
          );
          counts[day.id] = inGroups + standaloneExercises.length;
        } catch (error) {
          console.error(`Error al cargar ejercicios del día ${day.id}:`, error);
          counts[day.id] = 0;
        }
      });
      
      await Promise.all(exercisePromises);
      setExerciseCounts(counts);
    } catch (error) {
      console.error('Error al cargar días:', error);
      message.error('No se pudieron cargar los días de la rutina');
    } finally {
      setLoadingDays(false);
    }
  };

  const handleDelete = () => {
    modal.confirm({
      title: '¿Eliminar rutina?',
      content: `¿Estás seguro de que deseas eliminar "${routine?.title || 'esta rutina'}"?`,
      okText: 'Eliminar',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await deleteRoutine(id);
          message.success('Rutina eliminada');
          navigate('/routines/all');
        } catch (error) {
          message.error('No se pudo eliminar la rutina');
        }
      },
    });
  };

  const getImageSrc = () => {
    if (!routine) return null;
    const mediaPath = routine.preview_path || routine.media_path;
    return mediaPath ? resolveMedia(mediaPath) : null;
  };

  const renderDetailsTab = () => {
    if (!routine) return null;

    const imageSrc = getImageSrc();

    return (
      <div style={{ padding: '24px 0' }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <div></div>
          </Col>
          <Col>
            <Space>
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => navigate(`/routines/${id}/edit`)}
              />
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={handleDelete}
              />
            </Space>
          </Col>
        </Row>
        <Row gutter={[24, 24]}>
          <Col xs={24} md={8}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              {imageSrc ? (
                <img
                  src={imageSrc}
                  alt={routine.title || 'Rutina'}
                  style={{
                    width: '100%',
                    aspectRatio: '1',
                    borderRadius: 8,
                    objectFit: 'cover',
                  }}
                />
              ) : (
                <Avatar
                  shape="square"
                  size={200}
                  icon={<PictureOutlined />}
                  style={{ width: '100%', aspectRatio: '1' }}
                />
              )}

              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Row gutter={8}>
                  <Col>
                    <Text type="secondary" style={{ fontSize: 14 }}>Tipo Rutina</Text>
                  </Col>
                  <Col>
                    <Text strong>{routine.type?.name || routine.type?.code || '-'}</Text>
                  </Col>
                </Row>
                <Row gutter={8}>
                  <Col>
                    <Text type="secondary" style={{ fontSize: 14 }}>Enfoque</Text>
                  </Col>
                  <Col>
                    <Text strong>{routine.focus?.name || routine.focus?.code || '-'}</Text>
                  </Col>
                </Row>
                <Row gutter={8}>
                  <Col>
                    <Text type="secondary" style={{ fontSize: 14 }}>Duración</Text>
                  </Col>
                  <Col>
                    <Text strong>
                      {routine.duration ? `${routine.duration}` : '-'}
                    </Text>
                  </Col>
                </Row>
              </Space>
            </Space>
          </Col>

          <Col xs={24} md={16}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <Title level={2} style={{ margin: 0, fontSize: 24 }}>
                  {routine.title || 'Sin título'}
                </Title>
              </div>
              <div>
                <Title level={5} style={{ marginBottom: 8, color: '#8c8c8c' }}>
                  Descripción corta
                </Title>
                <Paragraph style={{ marginBottom: 0 }}>
                  {routine.short_description || 'No hay descripción corta disponible.'}
                </Paragraph>
              </div>

              <div>
                <Title level={5} style={{ marginBottom: 8, color: '#8c8c8c' }}>
                  Descripción
                </Title>
                <div
                  dangerouslySetInnerHTML={{
                    __html: routine.description || '<p>No hay descripción disponible.</p>',
                  }}
                  style={{
                    lineHeight: '1.6',
                    color: '#262626',
                  }}
                />
              </div>

              <div>
                <Title level={5} style={{ marginBottom: 8, color: '#8c8c8c' }}>
                  Instrucciones
                </Title>
                <div
                  dangerouslySetInnerHTML={{
                    __html: routine.instructions || '<p>No hay instrucciones disponibles.</p>',
                  }}
                  style={{
                    lineHeight: '1.6',
                    color: '#262626',
                  }}
                />
              </div>
            </Space>
          </Col>
        </Row>
      </div>
    );
  };
  
  // Agrupar días por semana
  const groupDaysByWeek = (days) => {
    const grouped = {};
    days.forEach((day) => {
      const week = day.week_number || 1;
      if (!grouped[week]) {
        grouped[week] = [];
      }
      grouped[week].push(day);
    });
    
    const sortedWeeks = Object.keys(grouped).sort((a, b) => Number(a) - Number(b));
    sortedWeeks.forEach((week) => {
      grouped[week].sort((a, b) => (a.day_number || 0) - (b.day_number || 0));
    });
    
    return { grouped, sortedWeeks };
  };

  // Abrir modal para agregar día
  const handleAddDay = () => {
    setEditingDay(null);
    form.resetFields();
    setModalVisible(true);
  };

  // Abrir modal para editar día
  const handleEditDay = (day) => {
    setEditingDay(day);
    form.setFieldsValue({
      title: day.title,
      day_number: day.day_number,
      week_number: day.week_number,
    });
    setModalVisible(true);
  };

  // Cerrar modal
  const handleCloseModal = () => {
    setModalVisible(false);
    setEditingDay(null);
    form.resetFields();
  };

  // Guardar día (crear o actualizar)
  const handleSaveDay = async () => {
    try {
      const values = await form.validateFields();
      
      // Convertir valores numéricos
      const payload = {
        title: values.title,
        day_number: Number(values.day_number),
        week_number: Number(values.week_number),
      };
      
      if (editingDay) {
        await updateRoutineDay(id, editingDay.id, payload);
        message.success('Día actualizado correctamente');
      } else {
        await createRoutineDay(id, payload);
        message.success('Día agregado correctamente');
      }
      
      handleCloseModal();
      await loadRoutineDays();
    } catch (error) {
      if (error.errorFields) {
        // Errores de validación del formulario
        return;
      }
      message.error(editingDay ? 'No se pudo actualizar el día' : 'No se pudo crear el día');
    }
  };

  // Eliminar día
  const handleDeleteDay = (day) => {
    modal.confirm({
      title: '¿Eliminar día?',
      content: `¿Estás seguro de que deseas eliminar "${day.title || 'este día'}"?`,
      okText: 'Eliminar',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await deleteRoutineDay(id, day.id);
          message.success('Día eliminado correctamente');
          await loadRoutineDays();
        } catch (error) {
          message.error('No se pudo eliminar el día');
        }
      },
    });
  };

  // Duplicar semana
  const handleDuplicateWeek = async (weekNumber) => {
    try {
      const result = await duplicateRoutineWeek(id, weekNumber);
      message.success(`Semana ${weekNumber} duplicada como Semana ${result?.week_number ?? ''}`.trim());
      await loadRoutineDays();
    } catch (error) {
      message.error('No se pudo duplicar la semana');
    }
  };

  const openDayDetail = (day) => {
    if (!day?.id) return;
    navigate(`/routines/${id}/days/${day.id}`);
  };

  // Publicar rutina
  const handlePublishRoutine = async () => {
    modal.confirm({
      title: '¿Publicar rutina?',
      content: `¿Estás seguro de que deseas publicar "${routine?.title || 'esta rutina'}"?`,
      okText: 'Publicar',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          setLoading(true);
          await publishRoutine(id);
          message.success('Rutina publicada correctamente');
          navigate('/routines/all');
        } catch (error) {
          message.error('No se pudo publicar la rutina');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const renderProgramTab = () => {
    const { grouped, sortedWeeks } = groupDaysByWeek(routineDays);

    return (
      <div style={{ padding: '24px 0' }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Title level={4} style={{ margin: 0, color: '#8c8c8c', fontWeight: 500 }}>
              Programa por día
            </Title>
          </Col>
          <Col>
            <Button
              type="primary"
              danger
              icon={<PlusOutlined />}
              onClick={handleAddDay}
            >
              Agregar día
            </Button>
          </Col>
        </Row>

        {loadingDays ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Text type="secondary">Cargando días...</Text>
          </div>
        ) : sortedWeeks.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Text type="secondary">No hay días programados. Agrega el primer día.</Text>
          </div>
        ) : (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {sortedWeeks.map((weekNum) => {
              const weekDays = grouped[weekNum] || [];
              return (
                <Card
                  key={weekNum}
                  style={{ borderRadius: 8 }}
                  bodyStyle={{ padding: '16px 24px' }}
                >
                  <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                    <Col>
                      <Title level={5} style={{ margin: 0, fontWeight: 600 }}>
                        Semana {weekNum}
                      </Title>
                    </Col>
                    <Col>
                      <Button
                        type="text"
                        icon={<CopyOutlined />}
                        onClick={() => handleDuplicateWeek(Number(weekNum))}
                      >
                        Duplicar Semana
                      </Button>
                    </Col>
                  </Row>
                  
                  <Divider style={{ margin: '12px 0' }} />
                  
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    {weekDays.map((day) => (
                      <Row
                        key={day.id}
                        align="middle"
                        style={{
                          padding: '12px',
                          backgroundColor: '#fafafa',
                          borderRadius: 4,
                          marginBottom: 8,
                        }}
                      >
                        <Col flex="auto">
                          <Space>
                            <Text
                              strong
                              style={{
                                textDecoration: 'underline',
                                fontSize: 16,
                                cursor: 'pointer',
                              }}
                              onClick={() => openDayDetail(day)}
                            >
                              {day.title}
                            </Text>
                            <Tag color="default">
                              Día {day.day_number}
                            </Tag>
                            <Tag color="default">
                              {exerciseCounts[day.id] ?? 0} ejercicio{exerciseCounts[day.id] !== 1 ? 's' : ''}
                            </Tag>
                          </Space>
                        </Col>
                        <Col>
                          <Space>
                            <Button
                              type="text"
                              icon={<EditOutlined />}
                              onClick={() => handleEditDay(day)}
                            />
                            <Button
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => handleDeleteDay(day)}
                            />
                          </Space>
                        </Col>
                      </Row>
                    ))}
                  </Space>
                </Card>
              );
            })}
          </Space>
        )}

        <RoutineDayModal
          visible={modalVisible}
          editingDay={editingDay}
          onCancel={handleCloseModal}
          onSave={handleSaveDay}
          form={form}
        />
      </div>
    );
  };

  const tabItems = [
    {
      key: 'details',
      label: 'Detalles rutina',
      children: renderDetailsTab(),
    },
    {
      key: 'program',
      label: 'Programa por día',
      children: renderProgramTab(),
    },
  ];

  if (loading) {
    return (
      <div style={{ padding: 24 }}>
        <Card loading={true} />
      </div>
    );
  }

  if (!routine) {
    return null;
  }

  return (
    <div style={{ padding: 24 }}>
      <Card
        style={{
          borderRadius: 8,
        }}
        loading={loading}
      >
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Space>
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate(-1)}
                style={{ padding: 0 }}
              />
              <Title level={3} style={{ margin: 0 }}>
                Configurar rutina
              </Title>
            </Space>
          </Col>
        </Row>

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
        />

        <Row justify="end" gutter={12} style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid #f0f0f0' }}>
          <Col>
            <Button
              onClick={() => navigate(-1)}
              style={{ backgroundColor: '#595959', color: '#fff', borderColor: '#595959' }}
            >
              Cancelar
            </Button>
          </Col>
          {/* <Col>
            <Button type="primary" danger>
              Guardar
            </Button>
          </Col> */}
          <Col>
            <Button 
              type="primary" 
              danger
              onClick={handlePublishRoutine}
              loading={loading}
            >
              Publicar rutina
            </Button>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default RoutineConfigurePage;

